import type {
  AgentCommand,
  AgentPromptInput,
  AgentEvent,
  AgentReconfigureOptions,
  AgentRuntimeConfig,
  ClientTransport,
  IAgentRuntime,
} from "@skaile/workspaces/types";

/**
 * Framework-agnostic protocol client for remote skaile agent servers.
 *
 * Implements `IAgentRuntime` over a pluggable `ClientTransport` (WebSocket, stdio,
 * in-process mock, etc.). Translates each `IAgentRuntime` method call into a typed
 * `AgentCommand` sent through the transport, and fans incoming `AgentEvent` messages
 * out to all handlers registered via `onEvent`. Key methods: `start()` optionally
 * connects the transport and sends initial configuration; `configure()` reconfigures
 * the agent at runtime; `prompt()` sends a user message; `sendStateAction()` pushes
 * an action to a shared state store; `dispose()` sends a shutdown command and
 * disconnects the transport.
 *
 * One `AgentClient` instance represents one connection to one agent server. Mapping
 * multiple session IDs to client instances is the consumer's responsibility (e.g.
 * `WsAgentGatewayService` in the platform backend uses `Map<sessionId, AgentClient>`).
 *
 * @example
 * ```ts
 * const transport = new WebSocketClientTransport({ url: 'ws://localhost:8080' });
 * const client = new AgentClient(transport);
 * await client.start({ projectDir: '/my/project' });
 * client.onEvent((e) => console.log(e));
 * await client.prompt('Hello');
 * await client.dispose();
 * ```
 *
 * @docLink packages/client/dev-guide#agent-client
 */
export class AgentClient implements IAgentRuntime {
  private readonly transport: ClientTransport;
  private readonly handlers = new Set<(event: AgentEvent) => void>();
  private readonly unsubTransport: () => void;

  constructor(transport: ClientTransport) {
    this.transport = transport;
    this.unsubTransport = transport.onMessage((event) => {
      for (const handler of this.handlers) {
        handler(event);
      }
    });
  }

  /** Connect transport (if needed) and send initial configuration. */
  async start(config: AgentRuntimeConfig): Promise<void> {
    if (!this.transport.connected) {
      await this.transport.connect();
    }
    await this.configure(config);
  }

  /**
   * @deprecated v2 reconfigure path. Protocol v3 replaces this with a one-shot
   * `SessionInitCommand` envelope sent at session start; mid-session reconfig is
   * routed through the `runner.*` capability surface. Kept for v2 callers; the
   * cast survives because the wire shape is no longer in the `AgentCommand`
   * union. Remove once forge apps have migrated to v3.
   */
  // @TODO: Code-Review: deprecated v2→v3 migration stub — remove once forge apps no longer call configure(); tracked as pre-publication cleanup
  async configure(config: AgentReconfigureOptions): Promise<void> {
    this.transport.send({ type: "configure", config } as unknown as AgentCommand);
  }

  /** Send a raw AgentCommand to the server. */
  send(command: AgentCommand): void {
    this.transport.send(command);
  }

  /** Send a user message to the agent. */
  async prompt(message: string): Promise<void> {
    this.transport.send({ type: "prompt", prompt: message });
  }

  async promptInput(input: AgentPromptInput): Promise<void> {
    this.transport.send({ type: "prompt", prompt: input.text, attachments: input.attachments });
  }

  /**
   * Answer an agent question. Pass `question` — verbatim from the `question`
   * event being answered — whenever the user can answer a multi-question call
   * out of order; without it the agent fills its next unanswered slot.
   */
  async reply(answer: string, question?: string, requestId?: string): Promise<void> {
    this.transport.send({
      type: "reply",
      answer,
      ...(question !== undefined ? { question } : {}),
      ...(requestId !== undefined ? { requestId } : {}),
    });
  }

  /** Cancel the current operation. */
  async abort(): Promise<void> {
    this.transport.send({ type: "cancel" });
  }

  /** Shut down the agent and disconnect transport. */
  async dispose(): Promise<void> {
    this.transport.send({ type: "shutdown" });
    this.unsubTransport();
    await this.transport.disconnect();
  }

  /**
   * @deprecated v2 shared-state mutation path. Protocol v3 routes shared-state
   * writes through the `runner.set_state` capability; this client method stays
   * as a stop-gap for v2 callers. The cast survives because `state_action` is
   * no longer in the `AgentCommand` union. Remove once forge apps have
   * migrated to v3.
   */
  // @TODO: Code-Review: deprecated v2 stop-gap with unsafe `as unknown as AgentCommand` cast — remove once forge apps use runner.set_state capability
  sendStateAction(store: string, action: Record<string, unknown>): void {
    this.transport.send({ type: "state_action", store, action } as unknown as AgentCommand);
  }

  onEvent(handler: (event: AgentEvent) => void): void {
    this.handlers.add(handler);
  }

  offEvent(handler: (event: AgentEvent) => void): void {
    this.handlers.delete(handler);
  }

  get isRunning(): boolean {
    return this.transport.connected;
  }
}
